import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CreditCard, 
  Calendar,
  Download,
  ExternalLink,
  CheckCircle2,
  Clock,
  ArrowRight,
  Shield
} from 'lucide-react';
import { format } from 'date-fns';
import { useBillingPage } from '../hooks/useBillingPage';
import { useCheckoutSession } from '../hooks/useCheckoutSession';
import { PricingCard } from '../components/billing/PricingCard';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatLocaleDate, formatLocaleCurrency } from '../utils/dateUtils';

// Define the BillingData interface based on usage
interface PlanDetails {
  amount: number;
  currency: string;
  interval: string;
}

interface SubscriptionDetails {
  status: string;
  plan: PlanDetails;
  current_period_end: number;
}

interface CardDetails {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface PaymentMethodDetails {
  card: CardDetails;
}

interface PlanOption {
  priceId: string;
  productName: string;
  description: string;
  formattedPrice: string;
  formattedMonthlyPrice?: string;
  buttonText: string;
  features: string[];
}

interface InvoiceDetails {
  id: string;
  number: string;
  amount_paid: number;
  currency: string;
  created: number;
  status: string;
  invoice_pdf: string;
  hosted_invoice_url: string;
}

interface BillingData {
  hasSubscription: boolean;
  portalUrl: string;
  subscription: SubscriptionDetails;
  payment_method: PaymentMethodDetails;
  planOptions: {
    monthly: PlanOption[];
    yearly: PlanOption[];
  };
  invoices?: InvoiceDetails[];
}

const Billing = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get billing period from URL or default to monthly
  const periodParam = searchParams.get('period');
  const initialPeriod = (periodParam === 'yearly' || periodParam === 'monthly') 
    ? periodParam 
    : 'monthly';
  
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>(initialPeriod as 'monthly' | 'yearly');
  // Use the hook without generic type
  const { data: billingData, isLoading, error } = useBillingPage();
  const checkoutMutation = useCheckoutSession();
  // Re-introduce derived hasSubscription, guarded by the billingData check later
  const hasSubscription = billingData?.hasSubscription;

  // Set document title
  useDocumentTitle();

  // Update URL when billing period changes
  useEffect(() => {
    navigate(`/billing?period=${billingPeriod}`, { replace: true });
  }, [billingPeriod, navigate]);

  const handlePeriodChange = (period: 'monthly' | 'yearly') => {
    setBillingPeriod(period);
  };

  const getCardIcon = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
        return '💳';
      default:
        return '💳';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-700';
      case 'canceled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
      <div className="w-[95%] mx-auto">
        {isLoading ? (
          // Skeleton Loader for Billing Page
          <div className="py-12 animate-pulse">
            {/* Header Skeleton */}
            <div className="mb-8">
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
            {/* Current Plan/Payment Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2 bg-gray-100 rounded-xl border border-gray-200 p-6 h-48"></div>
              <div className="bg-gray-100 rounded-xl border border-gray-200 p-6 h-48"></div>
            </div>
            {/* Toggle Skeleton */}
            <div className="flex justify-center mb-8">
              <div className="h-10 w-64 bg-gray-200 rounded-lg"></div>
            </div>
            {/* Pricing Cards Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-gray-100 rounded-xl border border-gray-200 p-6 h-96"></div>
              ))}
            </div>
            {/* Billing History Skeleton */}
            <div className="bg-gray-100 rounded-xl border border-gray-200 p-6 h-64"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center min-h-screen">
            <p className="text-red-600">Failed to load billing information. Please try again later.</p>
          </div>
        ) : !billingData ? (
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="py-12">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-bold text-gray-900 font-title mb-2">
                  Billing & Subscription
                </h1>
                <p className="mt-1 text-gray-500 font-body">
                  Manage your subscription and payment methods
                </p>
              </div>
              {hasSubscription && (
                <a
                  href={billingData.portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Manage Billing
                </a>
              )}
            </div>

            {!hasSubscription ? (
              <div className="bg-white rounded-xl border border-black shadow-lg p-6 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="w-5 h-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-gray-900">No Active Subscription</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  You currently don't have an active subscription. Choose a plan below to get started with Socialness.ai.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Current Plan Card */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-black shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-primary-600" />
                      <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(billingData.subscription.status)}`}>
                      {billingData.subscription.status === 'active' ? (
                        <>
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <Clock className="w-3 h-3 mr-1" />
                          {billingData.subscription.status}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="space-y-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-gray-900">
                        {formatLocaleCurrency(billingData.subscription.plan.amount, billingData.subscription.plan.currency, i18n.language)}
                      </span>
                      <span className="text-gray-500">/ {billingData.subscription.plan.interval}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>Current period ends on {formatLocaleDate(billingData.subscription.current_period_end * 1000, i18n.language, { dateFormat: 'PPP' })}</span>
                    </div>
                  </div>
                </div>
                {/* Payment Method Card */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                    <h2 className="text-lg font-semibold text-gray-900">Payment Method</h2>
                  </div>
                  <div className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg mb-4">
                    <div className="text-2xl">{getCardIcon(billingData.payment_method.card.brand)}</div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {billingData.payment_method.card.brand.toUpperCase()} •••• {billingData.payment_method.card.last4}
                      </p>
                      <p className="text-xs text-gray-500">
                        Expires {billingData.payment_method.card.exp_month}/{billingData.payment_method.card.exp_year}
                      </p>
                    </div>
                  </div>
                  <a
                    href={billingData.portalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                  >
                    Update payment method
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </div>
            )}

            {/* Billing Period Toggle */}
            <div className="flex justify-center items-center gap-8 mb-8">
              <div className="flex items-center gap-8 bg-primary-600 p-1 rounded-lg">
                <button
                  onClick={() => handlePeriodChange('monthly')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingPeriod === 'monthly'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-primary-600'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => handlePeriodChange('yearly')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingPeriod === 'yearly'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-primary-600'
                  }`}
                >
                  Yearly
                  <span className="ml-2 inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                    Save 20%
                  </span>
                </button>
              </div>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {billingData?.planOptions[billingPeriod].map((plan) => (
                <PricingCard
                  key={plan.priceId}
                  productName={plan.productName}
                  description={plan.description}
                  price={plan.formattedPrice}
                  monthlyPrice={plan.formattedMonthlyPrice}
                  buttonText={plan.buttonText}
                  features={plan.features}
                  onSubscribe={() => checkoutMutation.mutate({ priceId: plan.priceId })}
                  isLoading={checkoutMutation.isPending}
                  billingPeriod={billingPeriod}
                />
              ))}
            </div>

            {/* Billing History Card */}
            <div className="bg-white rounded-xl border border-black shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Billing History</h2>
              </div>

              {!hasSubscription ? (
                <div className="p-6 text-center text-gray-500">
                  No billing history available. Subscribe to a plan to view your invoices here.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoice Number
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="relative px-6 py-3">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {billingData.invoices?.map((invoice) => (
                        <tr key={invoice.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {invoice.number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatLocaleCurrency(invoice.amount_paid, invoice.currency, i18n.language)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatLocaleDate(invoice.created * 1000, i18n.language)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-3">
                              <a
                                href={invoice.invoice_pdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-700"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                              <a
                                href={invoice.hosted_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-700"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;